/**
 * P112: land tools (sim side, v0). Terrain reshaping on OWNED parcels through
 * additive effects owned in this file - deliberately NO edits to parcels.ts /
 * types.ts / balance.json (Mgmt-Sim P703 coordination; placeholder params live
 * here to avoid racing it).
 *
 * - pond: animals in enclosures on the parcel shed -1 stress daily.
 * - pasture: those animals are kept fed (fedUntilTick topped up to +12 ticks).
 * - shade: those animals shed -2 stress daily (stacks with pond).
 * - fence: fenceLevel 1..3 = the plan's tiers (basic/heavy/predator_proof,
 *   strength 1/2/3), priced PER METRE per plan (balance.json fence_per_m
 *   $8/$20/$45). Parcels carry hectares but no dimensions, so perimeter uses
 *   the documented SQUARE-PARCEL APPROXIMATION 4*sqrt(ha*10,000m) - flagged.
 *   fenceLevel is consumed for escape resolution in Mgmt-Sim's seam.
 *
 * All costs/rates are PLACEHOLDERS pending plan numbers. Purchases post to the
 * ledger under 'land'.
 */
import type { SimEngine, SystemContext } from '../core/engine';
import type { Tick } from '../../contracts/sim';
import { BinWriter, BinReader } from '../core/serialize';
import { CommandError, type MgmtState } from './types';
import { ledgerAppend } from './ledger';
import { ifloor, imax } from './rng';

export interface ParcelTerrain {
  pond: boolean;
  pasture: boolean;
  shade: boolean;
  fenceLevel: number;
}

export interface LandToolsState {
  tick: number;
  lastEffectDay: number;
  parcels: Record<string, ParcelTerrain>;
}

/** PLACEHOLDER costs/effects pending plan numbers. */
export const LAND_TOOL_COSTS = {
  pond: 50_000_00,
  pasture: 40_000_00,
  shade: 30_000_00,
} as const;
/** Plan fence_per_m (balance.json): basic $8, heavy $20, predator_proof $45. */
export const FENCE_PRICE_PER_M_CENTS: Record<number, number> = { 1: 800, 2: 2000, 3: 4500 };
/** Documented approximation (flagged): parcels have hectares, no dimensions -
 *  perimeter estimated as a square: 4 x sqrt(ha x 10,000 m^2). */
function isqrt(n: number): number {
  let lo = 0, hi = n;
  while (lo < hi) {
    const mid = ifloor((lo + hi + 1) / 2);
    if (mid * mid <= n) lo = mid; else hi = mid - 1;
  }
  return lo;
}
export function parcelPerimeterM(hectares: number): number {
  return imax(1, 4 * isqrt(ifloor(hectares * 10_000)));
}
/** Cost to raise a parcel's fence to `level` (1..3) at plan per-metre prices. */
export function fenceCostCents(hectares: number, level: number): number {
  const perM = FENCE_PRICE_PER_M_CENTS[level];
  if (perM === undefined) return 0;
  return parcelPerimeterM(hectares) * perM;
}
export const POND_STRESS_RELIEF = 1;
export const SHADE_STRESS_RELIEF = 2;
export const PASTURE_FED_TICKS = 12;
export const MAX_FENCE_LEVEL = 3;

const SERDE_MAGIC = 0x4c414e44; // 'LAND'
const SERDE_VERSION = 1;

/** Defensive mgmt read (P0B10 pattern); null when mgmt is absent. */
function mgmtOf(engine: unknown): MgmtState | null {
  try {
    return (engine as SimEngine).getSystemState('mgmt') as MgmtState;
  } catch {
    return null;
  }
}

function terrainFor(s: LandToolsState, parcelId: string): ParcelTerrain {
  let t = s.parcels[parcelId];
  if (!t) {
    t = { pond: false, pasture: false, shade: false, fenceLevel: 0 };
    s.parcels[parcelId] = t;
  }
  return t;
}

/** Animals living in enclosures on the given parcel. */
function animalsOn(mgmt: MgmtState, parcelId: string) {
  const encIds = new Set(mgmt.enclosures.filter(e => e.parcelId === parcelId).map(e => e.id));
  return mgmt.animals.filter(a => a.enclosureId !== null && encIds.has(a.enclosureId));
}

type LandToolKind = 'pond' | 'pasture' | 'shade' | 'fence';

export function registerLandToolsSim(engine: SimEngine): SimEngine {
  engine.registerSystem({
    name: 'landtools',
    init: (): LandToolsState => ({ tick: 0, lastEffectDay: 0, parcels: {} }),
    onTick(_ctx: SystemContext, s: LandToolsState, tick: Tick): void {
      s.tick = tick;
      const day = ifloor(tick / 24);
      if (day <= s.lastEffectDay) return;
      s.lastEffectDay = day;
      const ids = Object.keys(s.parcels);
      if (ids.length === 0) return;
      const mgmt = mgmtOf(engine);
      if (mgmt === null) return;
      for (const parcelId of ids) {
        const t = s.parcels[parcelId];
        if (!t.pond && !t.pasture && !t.shade) continue;
        const relief = (t.pond ? POND_STRESS_RELIEF : 0) + (t.shade ? SHADE_STRESS_RELIEF : 0);
        for (const a of animalsOn(mgmt, parcelId)) {
          if (relief > 0) a.stress = imax(0, a.stress - relief);
          if (t.pasture) a.fedUntilTick = imax(a.fedUntilTick, tick + PASTURE_FED_TICKS);
        }
      }
    },
    serialize(w: BinWriter, s: LandToolsState): void {
      w.u32(SERDE_MAGIC).u32(SERDE_VERSION);
      w.i32(s.tick).i32(s.lastEffectDay);
      const ids = Object.keys(s.parcels);
      w.u32(ids.length);
      for (const id of ids) {
        const t = s.parcels[id];
        w.str(id);
        w.u8((t.pond ? 1 : 0) | (t.pasture ? 2 : 0) | (t.shade ? 4 : 0));
        w.u8(t.fenceLevel);
      }
    },
    deserialize(r: BinReader): LandToolsState {
      if (r.peekU32() === SERDE_MAGIC) {
        r.u32();
        const version = r.u32();
        if (version === 1) {
          const tick = r.i32();
          const lastEffectDay = r.i32();
          const n = r.u32();
          const parcels: Record<string, ParcelTerrain> = {};
          for (let i = 0; i < n; i++) {
            const id = r.str();
            const flags = r.u8();
            parcels[id] = {
              pond: (flags & 1) !== 0,
              pasture: (flags & 2) !== 0,
              shade: (flags & 4) !== 0,
              fenceLevel: r.u8(),
            };
          }
          return { tick, lastEffectDay, parcels };
        }
        throw new Error(`landtools: unknown serde version ${String(version)}`);
      }
      // Legacy = pre-P112 blob: no land-tools tail. No tools existed.
      return { tick: 0, lastEffectDay: 0, parcels: {} };
    },
    collectUiState: (s: LandToolsState) => ({ parcels: JSON.parse(JSON.stringify(s.parcels)) as Record<string, ParcelTerrain> }),
  });

  const registerTool = (kind: LandToolKind): void => {
    engine.registerCommand({
      type: `land.${kind}`,
      validate(p) {
        if (typeof p !== 'object' || p === null) return 'payload must be an object';
        return typeof (p as Record<string, unknown>).parcelId === 'string' ? null : 'parcelId required';
      },
      execute(_ctx, payload) {
        const { parcelId } = payload as { parcelId: string };
        const mgmt = mgmtOf(engine);
        if (mgmt === null) return;
        const parcel = mgmt.parcels.find(p => p.id === parcelId);
        if (!parcel || !parcel.owned) return; // unknown/unowned parcel: silent no-op
        const s = engine.getSystemState('landtools') as LandToolsState;
        const t = terrainFor(s, parcelId);
        if (kind === 'fence') {
          if (t.fenceLevel >= MAX_FENCE_LEVEL) return; // maxed: silent no-op
        } else if (t[kind]) {
          return; // already built: silent no-op
        }
        const cost = kind === 'fence' ? fenceCostCents(parcel.hectares, t.fenceLevel + 1) : LAND_TOOL_COSTS[kind];
        if (mgmt.cash < cost) throw new CommandError(`insufficient cash: land.${kind} costs ${String(cost)} cents`);
        mgmt.cash -= cost;
        ledgerAppend(mgmt, { tick: s.tick, amount: -cost, category: 'land', refId: parcelId });
        if (kind === 'fence') t.fenceLevel += 1;
        else t[kind] = true;
      },
    });
  };
  registerTool('pond');
  registerTool('pasture');
  registerTool('shade');
  registerTool('fence');
  return engine;
}

/** Documented hook for Mgmt-Sim's escape seam: fence level on a parcel (0 if none). */
export function fenceLevelOf(engine: unknown, parcelId: string): number {
  try {
    const s = (engine as SimEngine).getSystemState('landtools') as LandToolsState;
    return s.parcels[parcelId]?.fenceLevel ?? 0;
  } catch {
    return 0;
  }
}
